// src/app/components/reporting/components/reports-phase.tsx
//
// Phase 3 of Insights & Reports: everything already composed.
//
// Source: handoff-reporting §6 and the "Reports" screen of
// `Insights and Reports - Polished.dc.html`.
//
// This replaces TWO surfaces: the History tab embedded in the old generator
// page, and `report-history-page.tsx`, a second, separate history screen that
// duplicated it. They rendered the same rows from the same endpoint with
// different filters and different card designs.

import { Clock } from "lucide-react";
import { Button, EmptyState, SearchInput, Spinner } from "@/app/components/kit";
import { sentenceCase } from "../format";
import { ConfidenceMeter } from "./report-draft";
import type { GeneratedReport } from "../types";

const ALL = "All";

export interface ReportsPhaseProps {
  reports: GeneratedReport[];
  isLoading: boolean;
  error: Error | null;
  search: string;
  onSearchChange: (v: string) => void;
  formatFilter: string;
  onFormatFilterChange: (v: string) => void;
  onOpen: (report: GeneratedReport) => void;
}

export function ReportsPhase({
  reports,
  isLoading,
  error,
  search,
  onSearchChange,
  formatFilter,
  onFormatFilterChange,
  onOpen,
}: ReportsPhaseProps) {
  // The format chips are the formats PRESENT, so a chip can never produce an
  // empty list, and a format nobody has used never appears.
  const formats = [ALL, ...Array.from(new Set(reports.map((r) => r.format))).sort()];

  const q = search.trim().toLowerCase();
  const shown = reports.filter((r) => {
    if (formatFilter !== ALL && r.format !== formatFilter) return false;
    if (!q) return true;
    return r.title.toLowerCase().includes(q) || r.prompt.toLowerCase().includes(q);
  });

  if (isLoading) return <Spinner label="Loading reports" />;

  if (error) {
    return <EmptyState icon={Clock} title="Reports could not be loaded" description={error.message} />;
  }

  if (reports.length === 0) {
    return (
      <EmptyState
        icon={Clock}
        title="No reports yet"
        description="Pick some insights in Library, describe what you need in Compose, and the finished report lands here."
      />
    );
  }

  return (
    <>
      <div className="upm-ir-repfilters">
        <div className="upm-ir-repfilters__search">
          <SearchInput
            value={search}
            placeholder="Search by title or prompt…"
            aria-label="Search reports by title or prompt"
            onChange={(e) => onSearchChange(e.target.value)}
            onClear={() => onSearchChange("")}
          />
        </div>
        {formats.map((f) => (
          <button
            key={f}
            type="button"
            className="upm-ir-filter"
            aria-pressed={formatFilter === f}
            onClick={() => onFormatFilterChange(f)}
          >
            {f === ALL ? f : sentenceCase(f)}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <p className="upm-ir-card__sub">No reports match this filter.</p>
      ) : (
        <div className="upm-ir-rows">
          {shown.map((r) => (
            <article className="upm-ir-row" key={r.id}>
              <div className="upm-ir-row__main">
                <div className="upm-ir-row__head">
                  <h3 className="upm-ir-row__title">{r.title}</h3>
                  <span className="upm-ir-chip upm-ir-chip--brand">{sentenceCase(r.format)}</span>
                  <span className="upm-ir-chip upm-ir-chip--reason">{sentenceCase(r.audience)}</span>
                </div>
                <p className="upm-ir-row__summary">{r.executive_summary}</p>
                <div className="upm-ir-row__meta">
                  <span>
                    {r.sections.length}
                    {r.sections.length === 1 ? " section" : " sections"} · {r.insight_count}
                    {r.insight_count === 1 ? " source" : " sources"}
                  </span>
                  <span className="upm-ir-card__sep">·</span>
                  <span>{longDate(r.created_at)}</span>
                </div>
              </div>
              <div className="upm-ir-row__side">
                <ConfidenceMeter confidence={r.confidence} compact />
                <Button variant="secondary" onClick={() => onOpen(r)}>
                  Open report
                </Button>
              </div>
            </article>
          ))}
        </div>
      )}
    </>
  );
}

/** An unparseable date renders as nothing rather than "Invalid Date". */
function longDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}
